import { ActionCableContext } from '@/providers/action-cable/action-cable.provider';
import {
  forgetPassword,
  resetPassword,
  sendCode,
  signIn,
  verifyCode,
} from '@/services/authentication-service/authentication.service';
import { useMutation } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { useJWT } from '../use-jwt/use-jwt';
import { useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { openNotification } from '@/utils/notification/notification';
import { AxiosErrorResponse } from '@/services/request/request.types';
import { useLogout } from '../use-logout/use-logout';

export const useAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useLogout();
  const { saveToken, bearerToken } = useJWT();
  const { createWSConnection } = useContext(ActionCableContext);

  const {
    mutateAsync: authUser,
    error: authError,
    isPending,
  } = useMutation({
    mutationFn: signIn,
    onSuccess: (data) => {
      if (!data.user?.mfa_required) {
        saveToken({ token: 'cookie' });
        createWSConnection();
        navigate('/');
      }
    },
    onError: (error: AxiosErrorResponse) => {
      navigate('/sign-in');
      if (error.response?.status === HttpStatusCode.Unauthorized) {
        openNotification({
          message:
            'We couldn’t find an account matching the email and password you entered. Please check your email and password and try again.',
          type: 'error',
        });
      }
      return error;
    },
  });

  // MFA: sending verification code
  const { mutateAsync: sendMFACode, isPending: isSendingMFACode } = useMutation({
    mutationFn: sendCode,
    onSuccess: (response) => {
      if ('success' in response && response.success === false) {
        openNotification({
          message: response.errors?.join('\n') || 'Something went wrong. Please try again.',
          type: 'error',
        });
        return;
      }
      openNotification({
        message: 'The verification code has been sent using your selected method (SMS or email).',
        type: 'success',
      });
    },
    onError: (error: AxiosError) => {
      openNotification({
        message: "Sorry, you can't use this verification method. Please choose a different one.",
        type: 'error',
      });
      return error;
    },
  });

  // MFA: verification code and signing in
  const { mutateAsync: verifyMFACode, isPending: isVerifyingMFACode } = useMutation({
    mutationFn: verifyCode,
    onSuccess: () => {
      saveToken({ token: 'cookie' });
      createWSConnection();
      const from = location.state?.from || '/';
      navigate(from, { replace: true });
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as any;

      if (errorData?.code_resent && errorData?.message) {
        openNotification({
          message: errorData.message,
          type: 'error',
        });
      } else if (errorData?.errors && Array.isArray(errorData.errors)) {
        openNotification({
          message: errorData.errors.join('\n') || 'Invalid verification code. Please try again.',
          type: 'error',
        });
      } else {
        openNotification({
          message: 'Invalid verification code. Please try again.',
          type: 'error',
        });
      }
      return error;
    },
  });

  const {
    mutate: sendEmailToResetPassword,
    error: errorSendingEmail,
    isPending: isPendingSendingEmail,
  } = useMutation({
    mutationFn: forgetPassword,
  });

  const {
    mutate: sendNewPassword,
    error: errorSendingNewPassword,
    isPending: isPendingSendingNewPassword,
  } = useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      navigate('/sign-in');
    },
  });

  const [errorChangePassword, setErrorChangePassword] = useState<string | null>(null);

  const { mutate: changePasswordForLoggedUser, isPending: isPendingChangePassword } = useMutation({
    mutationFn: resetPassword,
    onError: (error: AxiosErrorResponse) => {
      const errorMsg = String(Object.values(error?.response?.data ?? {})[0] || 'Unknown error');
      setErrorChangePassword(errorMsg);
      openNotification({
        message: errorMsg,
        type: 'error',
      });
    },
    onSuccess: () => {
      openNotification({
        message: 'Your password has been changed. Please log in again.',
        type: 'success',
      });
      logout();
    },
  });

  return {
    authUser,
    sendMFACode,
    verifyMFACode,
    isVerifyingMFACode,
    isSendingMFACode,
    authError,
    isAuthorized: !!bearerToken,
    isAuthorizing: isPending,
    sendEmailToResetPassword,
    errorSendingEmail,
    isPendingSendingEmail,
    changePasswordForLoggedUser,
    errorChangePassword,
    isPendingChangePassword,
    sendNewPassword,
    errorSendingNewPassword,
    isPendingSendingNewPassword,
  };
};
